/**
 * Encapsulate the logic necessary for doing some client side claculation on the dosage.
 *
 * This class capture the a prescribed dosage relative to the patient weight, calculates the absolute value this dosage
 * represent and provide options for rounding the value based on the vial size of the selected product.
 *
 * @type {Boolean}
 */
export class DosageArithmetic
{

    /**
     * @param  PatientRequestForm form Form that contains all the fields.
     * @param  string dosageFName Name of the field that contains the dosage.
     * @param  string absFName Name of the field that will display the absolute dosage.
     * @param  string productFName Name of the field that will be used to select the Product.
     * @param  string roundingFName Name of the field that indicate whatever the doage should be rounded up or down.
     * @param  mixed spreadFName Name of the field that contains the spread period or false, if none is provided.
     * @param  string unit Name of the unit for the absoluted dosage.
     */
    constructor(
        form,
        dosageFName,
        absFName,
        productFName,
        roundingFId,
        spreadFName = false,
        unit = 'g'
    ) {
        this.form = form;
        this.dosageFName = dosageFName;
        this.absFName = absFName;
        this.productFName = productFName;
        this.roundingFId = roundingFId;
        this.spreadFName = spreadFName;
        this.unit = unit;
    }

    /**
     * Initialise some event listening.
     */
    init() {
        console.log('DosageArithmetic init')

        // Build a list of field names that should trigger a recalculation of the absolute dosage.
        let fieldNames = [this.dosageFName, this.productFName, 'PatientWeight', 'PatientHeight', 'OptOutIbw', 'Patient[Gender]'];
        if (this.spreadFName !== false) {
            fieldNames = fieldNames.concat(this.spreadFName);
        }

        this.weightField = this.form.element.find('input[name=PatientWeight]')
        this.heightField = this.form.element.find('input[name=PatientHeight]')

        this.gender = this.form.element.find('select[name="Patient[Gender]"]').length ?
            this.form.element.find('select[name="Patient[Gender]"]') :
            this.form.element.find('input[name="Patient[Gender]"]')

        this.ibwField = this.form.element.find('input[name=IdealBodyWeight]')
        this.ddwField = this.form.element.find('input[name=DoseDeterminingWeight]')

        this.ibwField.prop("readonly", true);
        this.ddwField.prop("readonly", true);

        // Recalculate the value of the absolute maintenance dose

        this.form.element.on(
            'change',
            fieldNames.map(name => `[name="${name}"]`).join(','),
            this.recalculate.bind(this)
        );

        let reverseFieldNames = [this.absFName];
        this.form.element.on(
            'change',
            reverseFieldNames.map(name => `[name="${name}"]`).join(','),
            this.reverseCalculate.bind(this)
        );

    }

    /**
     * Force the value to be recaculated, but without resetting the rounding selection. This can be convenient when
     * processing an AJAX response from the form.
     */
    refresh() {
        this.recalculate(false);
    }

    /**
     * Get all the form fields with the provided names.
     * @param  string[] names List of names
     * @return JQuery Collection of Jquery elements.
     */
    fieldByName(...names) {
        return this.form.element.find(names.map(name => `[name="${name}"]`).join(','));
    }

    /**
     * Determine if we should use DDW or normal weight
     * @returns weight float
     */
    getPatientWeight() {
        const doseDeterminingWeight = this.getNumberFieldVal("DoseDeterminingWeight");

        if (!this.isOptOut() && doseDeterminingWeight && doseDeterminingWeight > 0) {
            return doseDeterminingWeight;
        }

        return this.getNumberFieldVal("PatientWeight");
    }

    isOptOut() {
        const ibwToggle = this.form.childElements('select[name=OptOutIbw]');

        return ibwToggle.length == 0 || ibwToggle.val() == 1;
    }

    /**
     * Recalculate the absolute daily dose.
     * @param bool unsetRadio=true Whatever the rounding selection should be invalidated.
     */
    recalculate(unsetRadio=true) {
        this.recalculateWeights()

        const dose = this.getNumberFieldVal(this.dosageFName);
        const weight = this.getPatientWeight();
        const spread = this.getNumberFieldVal(this.spreadFName);

        let absDose = dose * weight;
        if (spread > 0) {
            absDose = absDose / spread;
        }
        absDose = this.roundTo(absDose, 2);

        this.fieldByName(this.absFName).val(absDose);
        this.refreshRoundingField(absDose, unsetRadio);
    }

    recalculateWeights() {
        if(!this.gender.val() || this.gender.val() == '') {
            return null;
        }

        let ibw = this.recalculateIbw()

        if (!ibw || ibw <= 0) {
            return null;
        }

        // final value needs to be rounded
        this.ibwField.val(Math.round(ibw))

        let ddw = this.recalculateDdw(ibw)

        if (!ddw || ddw <= 0) {
            return null;
        }

        // final value needs to be rounded
        this.ddwField.val(Math.round(ddw))
    }

    recalculateIbw() {
        //IBW = 50 + [0.91 x (height (cm) – 152.4)]

        let height = this.heightField.val()

        if (!height) {
            return 0;
        }

        let genderPart = this.gender.val() == 'Female' ? 45.5 : 50;

        let part1 = (height - 152.4);
        let part2 = 0.91 * part1;
        let part3 = genderPart + part2;

        this.ibwCalculated = this.roundTo(part3, 2)

        return this.ibwCalculated
    }

    recalculateDdw() {
        //DDW = IBW + (0.4 x ABW (kg) – IBW (kg))

        let weight = this.weightField.val()

        if (!weight || !this.ibwCalculated) {
            return 0;
        }

        let part1 = (weight - this.ibwCalculated);
        let part2 = 0.4 * part1;
        let part3 = this.ibwCalculated + part2;

        this.ddwCalculated = this.roundTo(part3, 2)

        return this.ddwCalculated;
    }

    reverseCalculate(unsetRadio=true){
        const absDose = this.getNumberFieldVal(this.absFName);
        const weight = this.getPatientWeight();
        const spread = this.getNumberFieldVal(this.spreadFName);

        let dose = absDose / weight;
        if (spread > 0) {
            dose = dose * spread;
        }
        dose = this.roundTo(dose, 2);

        this.fieldByName(this.dosageFName).val(dose);
        this.refreshRoundingField(absDose, unsetRadio);
    }

    /**
     * Refresh the rounding options. The products are provided to DHB in vials of specific size. The full vial must be
     * provided to the patient, so the absolute value must be rounded down or up a vial size.
     *
     * When the calculated value changes, we must also invalidate the rounding selection and force the TMS to make a
     * counsious choice as to what direction we will be rounding in.
     *
     * This method will also be called when initialising the form. In this case the rounding selection should not be
     * invalidated.
     *
     * @param  Number dose Unrounded absolute dose.
     * @param  Boolean unsetRadio Whatever to invalidated the roudning selection.
     */
    refreshRoundingField(dose, unsetRadio) {
        // We pick the option set field by its ID.
        const field = this.form.element.find('#' + this.roundingFId);

        // uncheck the rounding field
        if (unsetRadio) {
            field.find('input').prop('checked', '').trigger('change');
        }

        // Get the vial size for the currently selected product.
        const productField = this.fieldByName(this.productFName);
        const productName = productField.val();
        const vialSize = this.getVialSize(productName);

        // Decide if we shoudl show the rounding options. Rounding options will be showed if:
        // * the dose is zero or
        // * we can't find a vial size or
        // * the dose falls exactly on the right vial size.
        if (dose == 0 || vialSize === undefined || this.vialModulus(dose, vialSize) == 0) {
            field.hide().find('input').prop('checked', '').trigger('change');
            return;
        }

        // Let's show the field.
        field.show();

        // Calculate how many vials we need with decimals.
        const numberOfVials = dose/vialSize;

        // Update labels with the rounded down and rounded up values.
        this.renderRoundRadio(field, 'Down', Math.floor(numberOfVials) * vialSize);
        this.renderRoundRadio(field, 'Up', Math.ceil(numberOfVials) * vialSize);

        // Relink the radio component on our field.
        this.form.updateRadioButtons(field);

        // Update help text
        field.find('.help-block').remove();
        const helpBlock = $(`<p><em>${productName}</em> is dispensed in vials of ${vialSize}g.</p>`)
            .addClass('help-block')
            .appendTo(field);
    }

    /**
     * Update the round down/up radio input
     * @param  Object field Jquery parent option set field
     * @param  string dir Direction we are rounding (either: Up or Down)
     * @param  Number val The rounded value
     */
    renderRoundRadio(field, dir, val) {
        // Round the value to 2 decimal to get around JS bad handling of floats.
        val = this.roundTo(val, 2);

        // Get the fields to operate on
        const input = field.find(`input[value="${dir}"]`);
        const parent = input.parent();

        // Update the label text and reattach the radio input
        parent
            .text(`Round ${dir.toLowerCase()} to ${val}${this.unit}`)
            .prepend(input)
            .attr('for', input.attr('id'));
    }

    /**
     * Try to retrieve a numeric value for a field. If the field can not be found or the value is not numeric. 0 is
     * returned
     * @param  string fieldName
     * @return float
     */
    getNumberFieldVal(fieldName) {
        let val = this.fieldByName(fieldName).val();
        val = parseFloat(val)
        if (isNaN(val)) {
            return 0;
        } else {
            return val;
        }
    }

    /**
     * Return the vial size for the provided product.
     * @param  string productName
     * @return float
     */
    getVialSize(productName) {
        // Vial size is a JS variable define directly on the HTML page.
        return vialSizes[productName]
    }

    /**
     * Round a value to the given number decimal.
     * @param  Number val Value to round.
     * @param  Number [decimal=0] Decimal to round to. Negative values can be used.
     * @return Rounded value.
     */
    roundTo(val, decimal = 0) {
        const multiplier = Math.pow(10, decimal);
        return Math.round(val * multiplier) / multiplier;
    }

    /**
     * Calculate the modulus of the dose from the vial size. This is needed, because some time the modulus round up to
     * some silly number, especially when dealing with decimals.
     * @param  Number dose
     * @param  Number vialSize
     * @return Number
     */
    vialModulus(dose, vialSize) {
        let mod = dose % vialSize;
        if (mod > 0) {
            mod = this.roundTo(mod, 2);
            mod = mod % vialSize;
        }
        return mod;

    }
}
